import { inject, Injectable } from '@angular/core';
import { EventType, MatrixEvent, Room, RoomMember } from 'matrix-js-sdk';
import { debounceTime, distinctUntilChanged, map, merge, Observable, shareReplay, startWith } from 'rxjs';
import { MatrixService } from '../matrix/matrix.service';
import { ChatStoreService } from '../chat-store/chat-store.service';
import { SyncService } from '../sync/sync.service';
import { Settings } from '../settings/settings.service';
import { ChatRoom } from '../../model/chat-room';

@Injectable({
  providedIn: 'root'
})
/**
 * Setzt den FullyReadMarker und sendet das receiptEvent für die Nachrichten. Des Weiteren wird ausgewertet, welche Nachricht
 * von allen gelesen wurden.
 *
 * A_25433-01 - Konfiguration von Public Read Receipts
 * A_26220 - Defaulteinstellung für Public Read Receipts
 * A_26221 - Private Read Receipts
 * A_25395-03 - Matrix Module
 */
export class ReceiptService {
  private lastSentFullyRead: Record<string, string> = {};
  private readonly matrixService = inject(MatrixService);
  private readonly chatStoreService = inject(ChatStoreService);
  private readonly syncService = inject(SyncService);

  /** PUBLIC: Die letzte eigene Nachricht, die alle gelesen haben */
  readonly lastReadByAll$: Observable<string | null> = merge(
    this.syncService.receipt$,
    this.chatStoreService.getCurrentRoomId$()
  ).pipe(
    debounceTime(200),
    map(() => this.computeLastReadByAll()),
    startWith(null),
    distinctUntilChanged(),
    shareReplay(1)
  );

  /**
   * Bestimmt für welches Event das m.read oder auch m.read.private gesendet werden soll.
   * Dies wird nur getriggert, wenn für das Event noch nie ein ReadReceipt gesendet wurde.
   * @param roomId
   * @param eventId
   */
  async markAsFullyRead(roomId: string, eventId: string): Promise<void> {
    const chatState = this.chatStoreService.getStateSync();
    const chatRoom = chatState.getCurrentRoom();
    if (!chatRoom) {
      return;
    }
    const foundMessage = chatRoom.messages.find(message => message.first.getId() === eventId);
    if (!foundMessage) {
      return;
    }
    if (this.lastSentFullyRead[roomId] === eventId) {
      return;
    }

    const readMarker = this.getReadMarker(roomId);
    if (readMarker && readMarker === eventId) {
      this.lastSentFullyRead[roomId] = eventId;
      return;
    }

    const newIdx = chatRoom.messages.findIndex(message => message.first.getId() === eventId);
    const oldIdx = chatRoom.messages.findIndex(message => message.first.getId() === readMarker);

    if (newIdx !== -1 && oldIdx !== -1 && newIdx <= oldIdx) {
      return;
    }
    try {
      const client = this.matrixService.client;
      if (Settings.getInstance().settingsConfigurations.receipts) {
        await client.setRoomReadMarkers(roomId, eventId, foundMessage.first);
      } else {
        await client.setRoomReadMarkers(roomId, eventId, undefined, foundMessage.first);
      }
      this.lastSentFullyRead[roomId] = eventId;
    } catch (e) {
      throw new Error('markAsFullyRead failed', { cause: e });
    }
  }

  getReadMarker(roomId: string): string | undefined {
    const matrixRoom = this.matrixService.client.getRoom(roomId);
    return matrixRoom?.getAccountData(EventType.FullyRead)?.getContent()['event_id'];
  }

  private computeLastReadByAll(): string | null {
    const chatState = this.chatStoreService.getStateSync();
    const room = this.matrixService.client.getRoom(chatState.currentRoomId);
    const chatRoom = chatState.getCurrentRoom();

    if (!room || !chatRoom) return null;

    const myId = this.matrixService.getUserId();
    const members = room.getJoinedMembers().filter(m => m.userId !== myId);

    if (members.length === 0) return null;

    const ownEvents = chatRoom.messages.filter(m => m.first.getSender() === myId).map(m => m.first);

    const positions = this.resolveReadPositions(members, room, ownEvents, chatRoom);

    if (positions.length < members.length) return null;

    const minIndex = Math.min(...positions);
    return ownEvents[minIndex]?.getId() ?? null;
  }

  private resolveReadPositions(
    members: RoomMember[],
    room: Room,
    ownEvents: MatrixEvent[],
    chatRoom: ChatRoom
  ): number[] {
    const allMessageEvents = chatRoom.messages.flatMap(message => message.messages);
    return members
      .map(m => room.getEventReadUpTo(m.userId))
      .filter(Boolean)
      .map(eid => this.resolveReplace(room, eid!))
      .filter(Boolean)
      .map(ev => {
        const index = allMessageEvents.findIndex(evt => evt.getId() === ev?.getId());
        if (index < 0) {
          return -1;
        }
        return [...ownEvents].findLastIndex(ownEvt => {
          const idx = allMessageEvents.findIndex(evt => {
            return evt.getId() === ownEvt.getId();
          });
          return idx <= index;
        });
      })
      .filter(i => i >= 0);
  }

  /** Auflösung eines Edit-Events (m.replace) */
  private resolveReplace(room: Room, eventId: string): MatrixEvent | null {
    const ev = room.findEventById(eventId);
    if (!ev) return null;

    const rel = ev.getContent()['m.relates_to'];
    if (rel?.rel_type === 'm.replace' && rel.event_id) {
      return room.findEventById(rel.event_id) ?? ev;
    }
    return ev;
  }
}
